<template>
  <div class="details-content">
    <div class="transaction-header">
      <div class="header-info">
        <div class="info-row">
          <span class="info-label">{{ $t('wagers.transId') }}:</span>
          <span class="info-value">{{ transaction.transaction.transaction_id }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">{{ $t('wagers.date') }}:</span>
          <span class="info-value">{{ formatDate(transaction.transaction.date) }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">{{ $t('wagers.reference') }}:</span>
          <span class="info-value">{{ transaction.transaction.reference }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">{{ $t('wagers.description') }}:</span>
          <span class="info-value">{{ $t('wagers.wager') }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">{{ $t('wagers.status') }}:</span>
          <span :class="['info-value', 'status', getTransactionStatusClass()]">
            {{ translateStatus(getTransactionStatus()) }}
          </span>
        </div>
      </div>
    </div>

    <template v-for="(games, gameKey) in transaction.games">
      <div v-for="game in games" :key="`${gameKey}-${game.draw_id}`" class="game-section">
        <div class="view-header">
          <h2>
            {{ game.name }} - {{ formatDate(game.draw_date) }}
            <span
              v-if="game.event_cancelled === 'yes'"
              class="event-cancelled-badge"
              >{{ $t('wagers.cancelled') }}</span
            >
          </h2>
        </div>

        <div class="wagers-card">
          <!-- Desktop Table View -->
          <div class="table-container desktop-only">
            <table class="wagers-table">
              <thead>
                <tr>
                  <th>{{ $t('transactionDetails.eventId') }}</th>
                  <th>{{ $t('transactionDetails.results') }}</th>
                  <th>{{ $t('wagers.totalBetAmount') }}</th>
                  <th>{{ $t('wagers.totalReturn') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{{ game.event_id || game.draw_id }}</td>
                  <td>
                    <div v-if="getWinningNumbers(game).length > 0" class="results-numbers">
                      <span 
                        v-for="(number, index) in getWinningNumbers(game)" 
                        :key="index"
                        :class="['number-ball', { 'bonus': isWinningBonusNumber(index, game) }]"
                      >
                        {{ number }}
                      </span>
                    </div>
                    <div v-else-if="game.has_results && isTransactionSettled" class="no-results">
                      {{ $t('wagers.pendingResults') }}
                    </div>
                    <div v-else class="no-results">
                      {{ $t('wagers.tbc') }}
                    </div>
                  </td>
                  <td>
                    {{ formatCurrency(transaction.transaction.total_cost, transaction.transaction.currency_symbol) }}
                  </td>
                  <td>
                    <span v-if="isTransactionSettled">
                      {{ formatCurrency(transaction.transaction.total_winnings, transaction.transaction.currency_symbol) }}
                    </span>
                    <span v-else>{{ $t('wagers.tbc') }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Mobile Card View -->
          <div class="mobile-card mobile-only">
            <div class="card-row">
              <span class="card-label">{{ $t('transactionDetails.eventId') }}:</span>
              <span class="card-value">{{ game.event_id || game.draw_id }}</span>
            </div>
            <div class="card-row">
              <span class="card-label">{{ $t('transactionDetails.results') }}:</span>
              <div class="card-value">
                <div v-if="getWinningNumbers(game).length > 0" class="results-numbers">
                  <span 
                    v-for="(number, index) in getWinningNumbers(game)" 
                    :key="index"
                    :class="['number-ball', { 'bonus': isWinningBonusNumber(index, game) }]"
                  >
                    {{ number }}
                  </span>
                </div>
                <div v-else-if="game.has_results && isTransactionSettled" class="no-results">
                  {{ $t('wagers.pendingResults') }}
                </div>
                <div v-else class="no-results">
                  {{ $t('wagers.tbc') }}
                </div>
              </div>
            </div>
            <div class="card-row">
              <span class="card-label">{{ $t('wagers.totalBetAmount') }}:</span>
              <span class="card-value">{{ formatCurrency(transaction.transaction.total_cost, transaction.transaction.currency_symbol) }}</span>
            </div>
            <div class="card-row">
              <span class="card-label">{{ $t('wagers.totalReturn') }}:</span>
              <span class="card-value">
                <span v-if="isTransactionSettled">
                  {{ formatCurrency(transaction.transaction.total_winnings, transaction.transaction.currency_symbol) }}
                </span>
                <span v-else>{{ $t('wagers.tbc') }}</span>
              </span>
            </div>
          </div>
        </div>

        <div class="view-header">
          <h2>
            {{ $t('transactionDetails.yourBets') }}
          </h2>
        </div>

        <div class="wagers-card">
          <!-- Desktop Table View -->
          <div class="table-container desktop-only">
            <table class="wagers-table">
              <thead>
                <tr>
                  <th>{{ $t('transactionDetails.betId') }}</th>
                  <th>{{ $t('transactionDetails.yourBet') }}</th>
                  <th>{{ $t('betslip.betType') }}</th>
                  <th>{{ $t('transactionDetails.odds') }}</th>
                  <th>{{ $t('transactionDetails.betAmount') }}</th>
                  <th>{{ $t('wagers.status') }}</th>
                  <th>{{ $t('transactionDetails.potentialReturns') }}</th>
                  <th>{{ $t('transactionDetails.actualReturns') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="bet in game.bets" :key="bet.bet_id">
                  <td>{{ bet.bet_id }}</td>
                  <td>
                    <span class="market-name">{{ bet.market_name }}:</span>

                    <span class="market-text">{{ bet.market_text }}</span>
                    <div class="selected-numbers">
                      <template v-for="(numbers, index) in bet.bet_numbers">
                        <template v-if="numbers.length > 0">
                          <span
                            v-for="number in numbers"
                            :key="`${index}-${number}`"
                            :class="['number-ball', { 'bonus': index === 1 }]"
                            >{{ number }}</span
                          >
                        </template>
                      </template>
                    </div>
                  </td>
                  <td>{{ bet.betType }}</td>
                  <td>{{ bet.odds }}</td>
                  <td>
                    {{ transaction.transaction.currency_symbol }}
                    {{ bet.bet_cost }}
                  </td>
                  <td>
                    <span
                      :class="['status', getBetStatusClass(bet, game)]"
                    >
                      {{ translateStatus(getBetStatus(bet, game)) }}
                    </span>
                  </td>
                  <td>
                    <span
                      v-if="getBetStatus(bet, game) === 'Cancelled'"
                      class="cancelled-amount"
                    >
                      {{ formatCurrency(0, transaction.transaction.currency_symbol) }}
                    </span>
                    <span v-else>
                      {{
                        formatCurrency(
                          bet.bet_cost * bet.odds,
                          transaction.transaction.currency_symbol
                        )
                      }}
                    </span>
                  </td>
                  <td>
                    <span
                      v-if="getBetStatus(bet, game) === 'Cancelled'"
                      class="cancelled-amount"
                    >
                      {{ formatCurrency(0, transaction.transaction.currency_symbol) }}
                    </span>
                    <span v-else>
                      {{
                        bet.winnings
                          ? formatCurrency(bet.winnings, transaction.transaction.currency_symbol)
                          : formatCurrency(0, transaction.transaction.currency_symbol)
                      }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Mobile Card View -->
          <div class="mobile-bets mobile-only">
            <div v-for="bet in game.bets" :key="bet.bet_id" class="bet-card">
              <div class="bet-card-header">
                <span class="bet-id">{{ $t('transactionDetails.betId') }}: {{ bet.bet_id }}</span>
                <span
                  :class="['status', getBetStatusClass(bet, game)]"
                >
                  {{ translateStatus(getBetStatus(bet, game)) }}
                </span>
              </div>
              
              <div class="bet-card-body">
                <div class="bet-info-row">
                  <span class="bet-label">{{ $t('transactionDetails.market') }}:</span>
                  <div class="bet-value">
                    <span class="market-name">{{ bet.market_name }}</span>
                    <span class="market-text">{{ bet.market_text }}</span>
                  </div>
                </div>

                <div v-if="bet.bet_numbers && (bet.bet_numbers[0]?.length > 0 || bet.bet_numbers[1]?.length > 0)" class="bet-info-row">
                  <span class="bet-label">{{ $t('betslip.numbersChosen') }}:</span>
                  <div class="bet-value">
                    <div class="selected-numbers">
                      <template v-for="(numbers, index) in bet.bet_numbers">
                        <template v-if="numbers.length > 0">
                          <span
                            v-for="number in numbers"
                            :key="`${index}-${number}`"
                            :class="['number-ball', { 'bonus': index === 1 }]"
                          >
                            {{ number }}
                          </span>
                        </template>
                      </template>
                    </div>
                  </div>
                </div>

                <div class="bet-info-row">
                  <span class="bet-label">{{ $t('betslip.betType') }}:</span>
                  <span class="bet-value">{{ bet.betType }}</span>
                </div>

                <div class="bet-info-row">
                  <span class="bet-label">{{ $t('transactionDetails.odds') }}:</span>
                  <span class="bet-value">{{ bet.odds }}</span>
                </div>

                <div class="bet-info-row">
                  <span class="bet-label">{{ $t('transactionDetails.betAmount') }}:</span>
                  <span class="bet-value">
                    {{ formatCurrency(bet.bet_cost, transaction.transaction.currency_symbol) }}
                  </span>
                </div>

                <div class="bet-info-row">
                  <span class="bet-label">{{ $t('transactionDetails.potentialReturns') }}:</span>
                  <span 
                    :class="['bet-value', { 'cancelled-amount': getBetStatus(bet, game) === 'Cancelled' }]"
                  >
                    {{
                      getBetStatus(bet, game) === 'Cancelled'
                        ? formatCurrency(0, transaction.transaction.currency_symbol)
                        : formatCurrency(
                            bet.bet_cost * bet.odds,
                            transaction.transaction.currency_symbol
                          )
                    }}
                  </span>
                </div>

                <div class="bet-info-row">
                  <span class="bet-label">{{ $t('transactionDetails.actualReturns') }}:</span>
                  <span 
                    :class="['bet-value', { 'cancelled-amount': getBetStatus(bet, game) === 'Cancelled' }]"
                  >
                    {{
                      getBetStatus(bet, game) === 'Cancelled'
                        ? formatCurrency(0, transaction.transaction.currency_symbol)
                        : bet.winnings
                          ? formatCurrency(bet.winnings, transaction.transaction.currency_symbol)
                          : formatCurrency(0, transaction.transaction.currency_symbol)
                    }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <div class="details-footer">
      <button
        type="button"
        class="rebet-button"
        @click="handleRebet"
      >
        {{ $t('wagers.rebet') }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { defineProps, defineEmits } from "vue";
import type { TransactionDetail } from "@/stores/transactions";
import { formatCurrency } from "@/utils/currency";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps<{
  transaction: TransactionDetail;
}>();

const emit = defineEmits<{
  (e: 'rebet'): void;
}>();

// Check if transaction is settled based on bet statuses
const isTransactionSettled = computed(() => {
  for (const gameKey in props.transaction.games) {
    const games = props.transaction.games[gameKey];
    for (const game of games) {
      for (const bet of game.bets) {
        const betStatus = getBetStatus(bet, game);
        if (betStatus !== 'Settled') {
          return false;
        }
      }
    }
  }
  return true;
});

const getTransactionStatus = () => {
  for (const gameKey in props.transaction.games) {
    const games = props.transaction.games[gameKey];
    for (const game of games) {
      if (game.event_cancelled === 'yes') {
        return 'Cancelled';
      }
      for (const bet of game.bets) {
        const betStatus = getBetStatus(bet, game);
        if (betStatus === 'Cancelled') {
          return 'Cancelled';
        }
        if (betStatus !== 'Settled') {
          return 'Open';
        }
      }
    }
  }
  return 'Settled';
};

const hasLostSettledBets = () => {
  for (const gameKey in props.transaction.games) {
    const games = props.transaction.games[gameKey];
    for (const game of games) {
      for (const bet of game.bets) {
        if (getBetStatus(bet, game) === 'Settled' && Number(bet.winnings || 0) <= 0) {
          return true;
        }
      }
    }
  }
  return false;
};

const getTransactionStatusClass = () => {
  const status = getTransactionStatus();
  if (status === 'Settled' && hasLostSettledBets()) {
    return 'settled-zero';
  }
  return status.toLowerCase();
};

const handleRebet = () => {
  emit('rebet');
};


const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const seconds = date.getSeconds().toString().padStart(2, '0');
  
  return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
};

const getBetStatus = (bet: any, game: any) => {
  // Check if the event is cancelled
  if (game.event_cancelled === "yes") {
    return "Cancelled";
  }
  // Check if the specific bet is cancelled
  if (bet.cancelled === true) {
    return "Cancelled";
  }
  // Return the original status if not cancelled
  return bet.status;
};

const translateStatus = (status: string) => {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'cancelled') return t('wagers.cancelled');
  if (normalized === 'settled') return t('wagers.settled');
  if (normalized === 'open') return t('wagers.open');
  return status;
};

const getBetStatusClass = (bet: any, game: any) => {
  const status = getBetStatus(bet, game);
  if (status === 'Settled' && Number(bet.winnings || 0) <= 0) {
    return 'settled-zero';
  }
  return status.toLowerCase();
};

const getWinningNumbers = (game: any) => {
  if (game?.results && Array.isArray(game.results) && game.results.length > 0) {
    const flatResults = game.results.flat();
    return flatResults;
  }
  return [];
};

const isWinningBonusNumber = (index: number, game: any) => {
  if (game?.results && Array.isArray(game.results) && game.results.length > 1) {
    const mainNumbers = game.results[0] || [];
    return index >= mainNumbers.length;
  }
  
  return false;
};
</script>

<script lang="ts">
export default {
  name: 'TransactionDetails',
}
</script>

<style scoped>
.details-content {
  padding: 0;
  width: 100%;
}

.details-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 20px 24px;
  margin-top: 16px;
  background: var(--bg-table-body, var(--bg-tertiary));
  border: 1px solid var(--border-color);
  border-radius: 8px;
}

.transaction-header {
  background: var(--bg-table-header, var(--bg-dark));
  padding: 20px 24px;
  margin-bottom: 16px;
  border-radius: 8px;
  border: 1px solid var(--border-color);
}

.header-info {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.info-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.info-label {
  color: var(--text-secondary);
  font-size: 12px;
  font-weight: 500;
}

.info-value {
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 600;
}

.info-value.status {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
  width: fit-content;
}

.info-value.status.open {
  background: color-mix(in srgb, var(--warning) 22%, var(--bg-card));
  color: var(--warning);
}

.info-value.status.settled {
  background: color-mix(in srgb, var(--success) 22%, var(--bg-card));
  color: var(--success);
}

.info-value.status.settled-zero {
  background: color-mix(in srgb, var(--error) 22%, var(--bg-card));
  color: var(--error);
}

.info-value.status.cancelled {
  background: color-mix(in srgb, var(--error) 22%, var(--bg-card));
  color: var(--error);
}

.game-section {
  /* border-radius: 8px; */
  overflow: hidden;
  background: var(--bg-card);
  /* border: 1px solid var(--border-color); */
}

.game-section:last-child {
  margin-bottom: 0;
}

.view-header {
  background: var(--bg-view-header, var(--bg-dark));
  padding: 16px 24px;
  margin-bottom: 1px;
  border-bottom: 1px solid var(--border-color);
}

.view-header h2 {
  color: var(--text-primary);
  font-size: 16px;
  font-weight: 500;
  margin: 0;
}

.result-section {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
}

.result-section h3 {
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 500;
  margin: 0;
  white-space: nowrap;
}

.result-section .selected-numbers {
  margin-top: 0;
}

.wagers-card {
  background: transparent;
  flex: 1;
  overflow: hidden;
  border: none;
  border-radius: 0;
  margin-bottom: 0;
  width: 100%;
}

.table-container {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.table-container::-webkit-scrollbar {
  display: none;
}

.desktop-only {
  display: block;
}

.mobile-only {
  display: none;
}

.wagers-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 800px;
  margin: 0 auto;
}

.wagers-table th {
  background: var(--bg-table-header, var(--bg-tertiary));
  color: var(--text-secondary);
  font-weight: normal;
  text-align: center;
  padding: 16px;
  font-size: 14px;
  /* border-bottom: 1px solid var(--border-color); */
}

.wagers-table td {
  padding: 16px;
  color: var(--text-primary);
  font-size: 14px;
  /* border-bottom: 1px solid var(--border-light); */
  text-align: center;
  vertical-align: top;
}

/* Specific styling for results column */
.wagers-table td:nth-child(2) {
  min-width: 200px; 
  max-width: 400px;
}

.wagers-table tr:last-child td {
  border-bottom: none;
}

.wagers-table tr:hover {
  background: var(--bg-table-body, var(--bg-tertiary));
}

.status {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  text-transform: uppercase;
  font-weight: 500;
}

.status.open {
  background: color-mix(in srgb, var(--warning) 14%, var(--bg-card));
  color: var(--warning);
}

.status.settled {
  background: color-mix(in srgb, var(--success) 14%, var(--bg-card));
  color: var(--success);
}

.status.settled-zero {
  background: color-mix(in srgb, var(--error) 14%, var(--bg-card));
  color: var(--error);
}

.status.cancelled {
  background: color-mix(in srgb, var(--error) 14%, var(--bg-card));
  color: var(--error);
}

.selected-numbers {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
  justify-content: center;
}

.results-numbers {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: center;
  margin-top: 0;
  max-width: 100%;
  min-height: 32px;
}

.no-results {
  color: var(--text-secondary);
  font-size: 12px;
  font-style: italic;
  text-align: center;
  min-height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}


.separator {
  color: var(--text-light);
  margin: 0 4px;
  display: flex;
  align-items: center;
}

.market-name {
  font-weight: bold;
  display: block;
  margin-bottom: 2px;
  color: var(--text-primary);
}

.market-text {
  font-size: 12px;
  color: var(--text-secondary);
}

.actions {
  display: flex;
  justify-content: flex-end;
  padding: 20px 24px;
  background: var(--bg-table-body, var(--bg-tertiary));
  border-top: 1px solid var(--border-color);
  border-radius: 0 0 8px 8px;
}

.rebet-button {
  background: var(--bg-rebet-button, var(--bg-success));
  color: var(--text-button, var(--text-white));
  border: none;
  padding: 8px 24px;
  border-radius: 13.1px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.rebet-button:hover {
  background: var(--bg-rebet-button, var(--bg-success-hover));
}

.rebet-button:focus-visible {
  outline: 2px solid var(--border-focus, var(--bg-primary));
  outline-offset: 2px;
}

.cancelled-amount {
  color: var(--text-light);
  text-decoration: line-through;
}

.event-cancelled-badge {
  background-color: var(--bg-tertiary);
  color: var(--bg-danger);
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  margin-left: 10px;
  white-space: nowrap;
}

/* Large screen improvements */
@media (min-width: 1200px) {
  .game-section {
    border-radius: 12px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  }
  
  .view-header {
    padding: 20px 28px;
  }
  
  .view-header h2 {
    font-size: 18px;
  }
  
  .wagers-table {
    min-width: 1000px;
  }
  
  .wagers-table th,
  .wagers-table td {
    padding: 20px 16px;
    font-size: 15px;
  }
  
  .actions {
    padding: 24px 28px;
  }

  .details-footer {
    padding: 24px 28px;
  }
  
  .rebet-button {
    padding: 12px 32px;
    font-size: 16px;
    border-radius: 8px;
  }
}

@media (max-width: 768px) {
  .desktop-only {
    display: none !important;
  }

  .mobile-only {
    display: block !important;
  }

  .transaction-header {
    padding: 16px;
    margin-bottom: 12px;
  }

  .header-info {
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }

  .info-label {
    font-size: 11px;
  }

  .info-value {
    font-size: 13px;
  }

  .view-header {
    padding: 12px 16px;
  }

  .view-header h2 {
    font-size: 14px;
  }

  .event-cancelled-badge {
    font-size: 10px;
    padding: 2px 6px;
    margin-left: 6px;
  }

  .result-section {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .result-section h3 {
    font-size: 13px;
  }

  .results-numbers {
    gap: 4px;
  }

  .status {
    padding: 3px 6px;
    font-size: 11px;
  }

  /* Mobile Card Styles */
  .mobile-card {
    padding: 16px;
    background: var(--bg-card);
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }

  .card-row {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .card-row:nth-child(2) {
    grid-column: 1 / -1;
  }

  .card-label {
    color: var(--text-secondary);
    font-size: 11px;
    font-weight: 500;
  }

  .card-value {
    color: var(--text-primary);
    font-size: 13px;
    font-weight: 600;
  }

  .mobile-bets {
    padding: 0;
  }

  .bet-card {
    background: var(--bg-card);
    border: 1px solid var(--border-color);
    border-radius: 8px;
    margin-bottom: 10px;
    overflow: hidden;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }

  .bet-card:last-child {
    margin-bottom: 0;
  }

  .bet-card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 16px;
    background: var(--bg-tertiary);
    border-bottom: 1px solid var(--border-color);
  }

  .bet-id {
    color: var(--text-primary);
    font-size: 13px;
    font-weight: 600;
  }

  .bet-card-body {
    padding: 12px 16px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px 12px;
  }

  .bet-info-row {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .bet-info-row:nth-child(1),
  .bet-info-row:nth-child(2) {
    grid-column: 1 / -1;
  }

  .bet-info-row:nth-child(1) {
    margin-bottom: 4px;
  }

  .bet-label {
    color: var(--text-secondary);
    font-size: 11px;
    font-weight: 500;
  }

  .bet-value {
    color: var(--text-primary);
    font-size: 13px;
    font-weight: 600;
  }

  .bet-value .market-name {
    display: block;
    font-weight: 600;
    margin-bottom: 4px;
  }

  .bet-value .market-text {
    font-size: 13px;
    color: var(--text-secondary);
    font-weight: 400;
  }

  .bet-value .selected-numbers {
    margin-top: 6px;
    justify-content: flex-start;
    gap: 4px;
  }

  .number-ball {
    width: 28px;
    height: 28px;
    font-size: 12px;
  }

  .results-numbers .number-ball {
    width: 26px;
    height: 26px;
    font-size: 11px;
  }

  .mobile-card .results-numbers {
    margin-top: 6px;
  }

  .actions {
    padding: 16px;
    margin-top: 8px;
  }

  .details-footer {
    padding: 16px;
    margin-top: 12px;
    justify-content: stretch;
  }

  .rebet-button {
    width: 100%;
    padding: 12px 24px;
  }

  .mobile-bets {
    padding: 0 0 8px 0;
  }
}
</style> 
